/**
 * Hook para listar tenants que o usuario tem acesso a chamados (com role
 * adequada). Usado pelo seletor de tenant em modos de escopo "org" ou "all"
 * (DevOps/Manager).
 *
 * Endpoint: GET /v1/me/tenants/with-chamados-access (TASK-2460)
 *
 * Spec: §9.2.4 do CHAMADOS_VISIBILIDADE_SPEC.md
 *
 * `enabled` = false por default — so e habilitado quando `scope !== 'own'`
 * (evita request desnecessario para usuarios em modo proprio).
 */

import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface TenantWithAccess {
  id: number
  name: string
  environment: string | null
  organization_id: number | null
  organization_name: string | null
}

interface UseDevOpsTenantListArgs {
  enabled?: boolean
}

const QUERY_KEY = ['user', 'tenants', 'with-chamados-access'] as const

export function useDevOpsTenantList({ enabled = false }: UseDevOpsTenantListArgs = {}) {
  return useQuery<TenantWithAccess[]>({
    queryKey: [...QUERY_KEY],
    queryFn: async () => {
      const res = await api.get<TenantWithAccess[]>('/v1/me/tenants/with-chamados-access')
      // Dedup defensivo por id — Mantine MultiSelect quebra se receber options
      // com value duplicado. Backend ja deduplica, mas mantemos cinto+suspensorio.
      const seen = new Set<number>()
      return res.data.filter((t) => {
        if (seen.has(t.id)) return false
        seen.add(t.id)
        return true
      })
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 min — lista raramente muda
  })
}
